import type { TenantScenarioStatusItem } from "@visioni/a3s-traffic-flow/fleet-messages";
import { AssetCameraKey, AssetId, CameraId, type SiteId } from "@visioni/a3s-schema";
import { formatCameraLabel } from "@src/utils/key-parser";
import { scenarioStatus, type ScenarioStatus } from "./worklist";

/**
 * One scenario on the config/map page, scoped to a single site. Unlike the old
 * `groupScenariosBySite`, this is built directly from the tenant scenario-status
 * list + the raw asset/camera keys — NO camera-list dependency (config-redesign
 * design doc: worklist/config both use "sites_plus_raw_keys"). It carries the
 * branded keys the save mutation (`usePutScenarioConfig`) and the data panel
 * (`useBuckets`) need, plus the current persisted config so the form can seed
 * itself and the map can draw the existing pin.
 */
export interface ConfigScenario {
  /** Stable list/React key: `${assetCameraKey}:${scenarioUuid}`. */
  key: string;
  scenarioUuid: string;
  /** Display name — friendly-name override if set, else the backend name. */
  scenarioName: string;
  /** Original backend name, used as the friendly-name input placeholder. */
  backendScenarioName: string;
  status: ScenarioStatus;
  /** True when the camera stopped reporting this scenario (soft-deleted). */
  stale?: boolean;
  /** True when the operator excluded this scenario from the roadsense report. */
  excluded?: boolean;
  /** Human asset/camera label, e.g. "11287 - Camera B". */
  assetCameraLabel: string;
  siteId: SiteId;
  assetId: AssetId;
  cameraId: CameraId;
  assetCameraKey: AssetCameraKey;
  /** Persisted config (undefined fields = unset), for seeding form + map pin. */
  coordinate?: { lat: number; lng: number };
  direction?: number;
  friendlyName?: string;
}

export interface SiteScenarios {
  /** All the site's scenarios, in first-appearance (backend) order. */
  scenarios: ConfigScenario[];
  /**
   * Distinct assets in the site, in first-appearance order. The config page
   * fetches a live location for each so the map can drop asset markers and
   * centre — this is the list to hand to `useAssetLocations`.
   */
  assetIds: AssetId[];
}

function assetCameraLabelFor(asset: AssetId, camera: CameraId): string {
  const label = formatCameraLabel(AssetCameraKey.toString(AssetCameraKey.fromParts(asset, camera)));
  // formatCameraLabel returns "" for assets it can't render (e.g. legacy app);
  // fall back to the raw composite so the operator still has an identifier.
  return label !== "" ? label : `${AssetId.toString(asset)}@${CameraId.toString(camera)}`;
}

/**
 * Extracts every scenario belonging to `siteId` from the tenant-wide list.
 *
 * The config page is always scoped to one site (the URL always carries a site),
 * so we filter the tenant list down here rather than re-fetching a site-scoped
 * endpoint — the tenant list is already loaded for the worklist and is the
 * single source of truth for scenario config. Rows from other sites are
 * ignored; an unknown site simply yields empty results (the screen renders an
 * empty/So-such-site state).
 */
export function buildSiteScenarios(items: TenantScenarioStatusItem[], siteId: string): SiteScenarios {
  const scenarios: ConfigScenario[] = [];
  const assetIds: AssetId[] = [];
  const seenAssets = new Set<string>();

  for (const item of items) {
    if (String(item.site) !== siteId) continue;

    const assetKey = AssetId.toString(item.asset);
    if (!seenAssets.has(assetKey)) {
      seenAssets.add(assetKey);
      assetIds.push(item.asset);
    }

    const assetCameraKey = AssetCameraKey.fromParts(item.asset, item.camera);
    const assetCameraKeyStr = AssetCameraKey.toString(assetCameraKey);
    const assetCameraLabel = assetCameraLabelFor(item.asset, item.camera);

    for (const scenario of item.scenarios) {
      const friendly = scenario.friendlyName;
      scenarios.push({
        key: `${assetCameraKeyStr}:${scenario.scenarioUuid}`,
        scenarioUuid: scenario.scenarioUuid,
        scenarioName: friendly && friendly.trim() !== "" ? friendly : scenario.scenarioName,
        backendScenarioName: scenario.scenarioName,
        status: scenarioStatus(scenario),
        ...(scenario.stale && { stale: true }),
        ...(scenario.excluded && { excluded: true }),
        assetCameraLabel,
        siteId: item.site,
        assetId: item.asset,
        cameraId: item.camera,
        assetCameraKey,
        coordinate: scenario.coordinate,
        direction: scenario.direction,
        friendlyName: friendly,
      });
    }
  }

  return { scenarios, assetIds };
}
